import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import type { PageSeoTables } from "@/data/pageSeoTables";

/* the SEO table the build writes (scripts/prerender-head.tsx), fetched once;
   the dev server, with no build, makes it from the data. The page's HTML
   already carries its head, so nothing waits for this. */
let tables: Promise<PageSeoTables> | null = null;
const loadTables = () =>
  (tables ??= import.meta.env?.DEV
    ? import("@/data/pageSeoTables").then((m) => m.default)
    // no-cache: always revalidated (a 304 when unchanged). Its name is the same
    // every build, yet /assets/ is served "immutable" for a year, so a browser
    // would otherwise keep the first copy it saw (2026-11-02)
    : fetch("/assets/page-seo.json", { cache: "no-cache" }).then((r) => (r.ok ? r.json() : Promise.reject(r.status))));

/**
 * Holds a page's title, meta description and keywords at the values the site's
 * SEO sets for it: hand-written SEO in src/data/pageSeo.ts first, else the
 * centre template (scripts/prerender/centre-seo.ts, via the generated
 * src/data/centreHeads.ts). A page with only keywords of its own (PAGE_KEYWORDS)
 * keeps its title and description and has those keywords held. Pages with
 * none of these are left alone.
 *
 * Many pages set document.title and the meta description themselves — some only
 * after their content loads — so setting them once is not enough: the head is
 * watched while the page is open and put back if the page changes it.
 */
function setMeta(attr: "name" | "property", key: string, content: string) {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  if (el.getAttribute("content") !== content) el.setAttribute("content", content);
}

export default function PageSeoScope() {
  const { pathname } = useLocation();

  useEffect(() => {
    let stop = () => {};
    let cancelled = false;
    loadTables().then(
      (t) => { if (!cancelled) stop = hold(t, pathname); },
      () => { tables = null; } // tried again on the next page
    );
    return () => { cancelled = true; stop(); };
  }, [pathname]);

  return null;
}

/** keeps the head at the table's values for this page; returns what stops it */
function hold(t: PageSeoTables, pathname: string): () => void {
  const path = pathname.replace(/\/+$/, "") || "/";
  const seo = t.seo[path];
  const ownKeywords = t.keywords[path];
  const pickedOg = t.og?.[path];
  if (!seo && !ownKeywords && !pickedOg) return () => {};
  const keywords = (seo?.keywords ?? ownKeywords)?.join(", ");

  const apply = () => {
    if (seo) {
      if (document.title !== seo.title) document.title = seo.title;
      setMeta("name", "description", seo.description);
      setMeta("property", "og:title", seo.title);
      setMeta("property", "og:description", seo.description);
    }
    const image = seo?.ogImage ?? pickedOg;
    if (image) {
      setMeta("property", "og:image", image);
      setMeta("name", "twitter:image", image);
      // and on the page's MedicalWebPage, as the prerender wrote it (rewritten only when it differs)
      for (const s of document.head.querySelectorAll<HTMLScriptElement>('script[type="application/ld+json"]')) {
        if (!s.text.includes('"MedicalWebPage"')) continue;
        try {
          const node = JSON.parse(s.text);
          if (node["@type"] !== "MedicalWebPage" || node.primaryImageOfPage?.url === image) continue;
          const pic = { "@type": "ImageObject", url: image };
          s.text = JSON.stringify({ ...node, image: pic, primaryImageOfPage: pic });
        } catch { /* not ours to fix */ }
      }
    }
    if (keywords) setMeta("name", "keywords", keywords);
  };
  apply();
  // the page's own effects run after this one; catch what they set
  const timer = window.setTimeout(apply, 0);
  const observer = new MutationObserver(apply);
  observer.observe(document.head, {
    childList: true,
    subtree: true,
    characterData: true,
    attributes: true,
    attributeFilter: ["content"],
  });
  return () => {
    window.clearTimeout(timer);
    observer.disconnect();
  };
}
